"use client"

import { cn } from "@/lib/utils"
import type { ThemeRoute } from "@/utils/themeColors"
import {
  getThemeBackgroundColor,
  getThemeBadgeBackground,
} from "@/utils/themeColors"

export interface TaxonomySection {
  label: string
  terms: string[]
}

interface TaxonomySidebarProps {
  sections: TaxonomySection[]
  metadata?: Array<{
    label: string
    value: string | boolean | undefined
    type?: "text" | "boolean" | "email" | "badge"
  }>
  className?: string
  themeColor?: ThemeRoute
}

export default function TaxonomySidebar({
  sections,
  metadata,
  className,
  themeColor,
}: TaxonomySidebarProps) {
  const themeBgClass = themeColor ? getThemeBackgroundColor(themeColor) : ""
  const badgeBgClass = themeColor
    ? getThemeBadgeBackground(themeColor)
    : "bg-muted"

  return (
    <div
      className={cn(
        "border border-border rounded p-6 space-y-6",
        themeBgClass,
        className
      )}
    >
      {/* Taxonomy sections */}
      {sections.map(
        (section, idx) =>
          section.terms.length > 0 && (
            <div key={idx}>
              <h3 className="font-bold text-sm mb-3 text-foreground">
                {section.label}
              </h3>
              <div className="flex flex-wrap gap-2">
                {section.terms.map((tag, tagIdx) => {
                  // Ensure tag is always a string to prevent React error #418
                  const safeTag = typeof tag === 'string' ? tag : String(tag || '')
                  return (
                    <span
                      key={tagIdx}
                      className={cn(
                        "inline-block text-muted-foreground px-3 py-1 rounded text-xs border-0",
                        badgeBgClass
                      )}
                    >
                      {safeTag}
                    </span>
                  )
                })}
              </div>
            </div>
          )
      )}

      {/* Metadata sections */}
      {metadata &&
        metadata.map(
          (item, idx) =>
            item.value !== undefined &&
            item.value !== null &&
            item.value !== "" && (
              <div key={idx}>
                <h3 className="font-bold text-sm mb-3 text-foreground">
                  {item.label}
                </h3>
                {(() => {
                  const type =
                    item.type ||
                    (typeof item.value === "boolean" ? "boolean" : "text")
                  switch (type) {
                    case "boolean":
                      // Ensure boolean values are converted to string to prevent React error #418
                      const booleanText = item.value === true ? "Yes" : item.value === false ? "No" : String(item.value || "No")
                      return (
                        <span
                          className={cn(
                            "inline-block text-muted-foreground px-3 py-1 rounded text-xs border-0",
                            badgeBgClass
                          )}
                        >
                          {booleanText}
                        </span>
                      )
                    case "email":
                      return typeof item.value === "string" ? (
                        <a
                          href={`mailto:${item.value}`}
                          className="text-sm text-primary hover:text-primary/80 underline break-all"
                        >
                          {item.value}
                        </a>
                      ) : null
                    case "badge":
                      return typeof item.value === "string" ? (
                        <span
                          className={cn(
                            "inline-block text-muted-foreground px-3 py-1 rounded text-xs border-0",
                            badgeBgClass
                          )}
                        >
                          {item.value}
                        </span>
                      ) : null
                    case "text":
                    default:
                      return (
                        <p className="text-sm text-muted-foreground break-words">
                          {String(item.value)}
                        </p>
                      )
                  }
                })()}
              </div>
            )
        )}
    </div>
  )
}
